import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthState, LoginOptions } from './types';
import { authManager } from './AuthManager';

// Create the authentication context
const AuthContext = createContext<{
  state: AuthState;
  login: (options?: LoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  isAuthEnabled: boolean;
  providerName: string;
} | null>(null);

/**
 * Authentication provider component that wraps the app
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(authManager.getState());
  const isAuthEnabled = authManager.isAuthenticationEnabled();
  const providerName = authManager.getProviderName();

  // Update state when auth manager state changes
  useEffect(() => {
    const unsubscribe = authManager.addEventListener((event) => {
      const newState = authManager.getState();
      setState(newState);
    });

    return unsubscribe;
  }, []);

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authManager.initialize();
      } catch (error) {
        logger.error('Failed to initialize authentication:', error);
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (options?: LoginOptions) => {
    try {
      await authManager.login(options);
    } catch (error) {
      logger.error('Login failed:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authManager.logout();
    } catch (error) {
      logger.error('Logout failed:', error);
      throw error;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        login,
        logout,
        isAuthEnabled,
        providerName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Hook to get current user information
 */
export const useUser = () => {
  const { state } = useAuth();
  return state.user;
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = () => {
  const { state, isAuthEnabled } = useAuth();
  
  // If auth is disabled, consider user as authenticated
  if (!isAuthEnabled) {
    return true;
  }
  
  return state.isAuthenticated;
};

/**
 * Hook to get authentication loading state
 */
export const useAuthLoading = () => {
  const { state } = useAuth();
  return state.isLoading;
};

/**
 * Hook to get authentication error
 */
export const useAuthError = () => {
  const { state } = useAuth();
  return state.error;
}; 